import { FC, useContext, useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";

import AuthSubHeader from "src/components/authSubHeader";
import FooterText from "src/components/footerText";
import CaseStepOne from "src/components/caseStepOne";
import CaseStepTwo from "src/components/caseStepTwo";
import CaseStepThree from "src/components/caseStepThree";

import { AddCaseProps } from "src/pages/addCase/types";
import { AddCaseSchema } from "src/validation/addCase";
import { AuthContext } from "src/context/authContext";
import { ToastContext } from "src/context/toastContext";
import { ToastTypes } from "src/types";
import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import { CaseClonePathParams, CaseCloneResponseType } from "./types";
import { LoaderContext } from "src/context/loaderContext";
import { populatingCloneCase } from "src/helpers/cloneCase";
import { UserType } from "src/types/users";
import RestoreCaseSessionModal from "./modal";
import { DEFAULT_CASE_ATTRIBUTES } from "src/constants/addCase";
import useCaseSubmitFunc from "src/hooks/useCaseSubmitFunc";
import { PracticeDropdownResponse } from "src/components/caseStepTwo/types";
import UploadClinicalModal from "src/components/modals/transferCaseDialogs/uploadClinicalModal";
import StepThreeConfirmationModal from "src/components/modals/stepThreeConfirmationModal";





const HubCaseClone: FC = (): JSX.Element => {
  const [step, changeStep] = useState<number>(1);
  const [isModalVisible, showHideModal] = useState<boolean>(false);
  const [isStepTwoMounted, mountingStepTwo] = useState<boolean>(false);
  const [isStepOneInvalid, setStepOneValidity] = useState<boolean>(false);
  const [isStepTwoInvalid, setStepTwoValidity] = useState<boolean>(false);
  const [precertInfoModal, setPrecertInfoModal] = useState<{ isModalVisible: boolean; caseId: number; precertId: number; }>({
    isModalVisible: false,
    caseId: 0,
    precertId: 0,
  })
  const [shouldShowModal, setShouldShowModal] = useState<boolean>(false)
  const [isAlreadyShown, setIsAlreadyShown] = useState<boolean>(false)

  const formRef = useRef<HTMLFormElement>(null)
  const { userData, reinvestigation, outreach, pcc_outreach } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);
  const { setLoader,hideLoader } = useContext(LoaderContext);
  const navigate = useNavigate();
  const { caseId } = useParams();
  const { data: practiceDropdownApiRes } = useFetch<PracticeDropdownResponse>(APIEndpoints.PRACTICE_DROPDOWN);
  const { addCaseLoading, caseDataSubmission } = useCaseSubmitFunc(practiceDropdownApiRes, setPrecertInfoModal)

  const { control, formState, handleSubmit, getValues, watch, trigger, setValue, reset } = useForm<AddCaseProps>({
    mode: "all",
    resolver: yupResolver(AddCaseSchema),
    defaultValues: {
      ...DEFAULT_CASE_ATTRIBUTES,
      user_id: userData ? userData.id : 0,
      creator_name: userData ? userData.name : "",
      patient_attributes: {
        ...DEFAULT_CASE_ATTRIBUTES.patient_attributes,
        reinvestigation, outreach, pcc_outreach
      }
    }
  });

  const patientData = watch("patient_attributes");
  const prescriptionData = watch("prescription_attributes");
  const pcc_outreach_value = watch("patient_attributes.pcc_outreach");
  const pcc_value = watch("patient_attributes.outreach");



  useEffect(() => {
    const data = localStorage.getItem('newCase');
    if (data) showHideModal(true)
  }, []);

  useEffect(() => {
    if (patientData && patientData.global_patient_id)
      trigger(["patient_attributes", "insurances_attributes"]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientData]);

  const removeCurrentData = () => {
    localStorage.removeItem('newCase');
    reset();
    showHideModal(false);
  };

  const restoringCaseData = () => {
    const data = localStorage.getItem('newCase');
    if (data) {
      navigate("/cases/new", { state: { fromClone: true } })
    }
    showHideModal(false);
  };

  // useEffect(() => {
  //   if (step !== 3) return;
  //   if (pcc_outreach_value && pcc_value) return;
  //   if (["Other", "PHN"].includes(prescriptionData.indication)) return;
  //   setShouldShowModal(true);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [pcc_outreach_value, pcc_value]);




  const { error: caseCloneError, data: caseCloneData, loading: caseCloneLoading } =
    useFetch<CaseCloneResponseType, {}, CaseClonePathParams>(APIEndpoints.CLONE_CASE_DETAILS, {}, { caseId: caseId ? Number(caseId) : 0 });

  useEffect(() => {
    if (caseCloneLoading === true) {
      setLoader();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseCloneLoading]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (caseCloneData || caseCloneError) {
      if(caseCloneData) {
        //console.log(caseCloneData)
        timeout = setTimeout(() => {
          const reinvestigation = practiceDropdownApiRes?.data?.[0]?.reinvestigation === "1" || practiceDropdownApiRes?.data?.[0]?.reinvestigation === true ? true : false;
          const outreach = practiceDropdownApiRes?.data?.[0]?.outreach === "1" || practiceDropdownApiRes?.data?.[0]?.outreach === true ? true : false;
          const pcc_outreach = practiceDropdownApiRes?.data?.[0]?.pcc_outreach === "1" || practiceDropdownApiRes?.data?.[0]?.pcc_outreach === true ? true : false;
          const caseFormData: AddCaseProps = populatingCloneCase(caseCloneData, userData || {} as UserType, getValues, reinvestigation, outreach, pcc_outreach);
          reset(caseFormData);
          hideLoader();
        }, 500);
      }
      if (caseCloneError) {
        hideLoader();
      }
    }

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseCloneData, caseCloneError]);





  useEffect(() => {
    const { errors } = formState;

    if (Object.keys(errors).length > 0) {

      if (Object.hasOwn(errors, 'prescription_attributes') || Object.hasOwn(errors, 'shipments_attributes')) {
        setStepOneValidity(true);
      } else {
        setStepOneValidity(false);
      }

      if (Object.hasOwn(errors, 'patient_attributes') ||
        Object.hasOwn(errors, 'case_doctor') ||
        Object.hasOwn(errors, 'insurances_attributes')
      ) {
        setStepTwoValidity(true);
      } else {
        setStepTwoValidity(false);
      }
    } else {
      setStepOneValidity(false);
      setStepTwoValidity(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState]);

  useEffect(() => {
    const messages = [];
    const { errors } = formState;

    if (isStepOneInvalid && isStepTwoInvalid && step >= 2) {
      messages.push(`The following sections from step 1 have errors:
      ${errors.prescription_attributes && errors.prescription_attributes ? '<br>- case procedure<br>' : ''}
      ${errors.prescription_attributes ? '- case prescription<br>' : ''}
      ${errors.shipments_attributes ? '- case prescription shipments' : ''}`);

      messages.push(`The following sections from step 2 have errors: <br>
      ${errors.doctor_attributes ? '- case doctor<br>' : ''}
      ${errors && errors.patient_attributes ? '- case patient<br>' : ''}
      ${errors.patient_attributes && errors.patient_attributes.phone_numbers ? '- case patient phone numbers<br>' : ''}
      ${errors.patient_attributes && errors.patient_attributes.address ? '- case patient address<br>' : ''}
      ${errors.patient_attributes && errors.patient_attributes.address ?'- case patient entities address' : ''}
      ${errors.insurances_attributes ? '<br>- case patient insurances' : ''}`);
    }
    else if (isStepOneInvalid && step >= 1) {
      messages.push(`The following sections from step 1 have errors:
      ${errors.procedure_attributes && errors.procedure_attributes.cpt_code ? '<br>- case procedure<br>' : ''}
      ${errors.prescription_attributes ? '- case prescription<br>' : ''}
      ${errors.shipments_attributes ? '- case prescription shipments' : ''}`);
    }
    else if (isStepTwoInvalid && step >= 2) {
      messages.push(`The following sections from step 2 have errors: <br>
      ${errors.doctor_attributes ? '- case doctor<br>' : ''}
      ${errors && errors.patient_attributes ? '- case patient<br>' : ''}
      ${errors.patient_attributes && errors.patient_attributes.phone_numbers ? '- case patient phone numbers<br>' : ''}
      ${errors.patient_attributes && errors.patient_attributes.address ? '- case patient address<br>' : ''}
      ${errors.patient_attributes && errors.patient_attributes.address ?'- case patient entities address' : ''}
      ${errors.insurances_attributes ? '<br>- case patient insurances' : ''}`);
    }

    showToast(messages, ToastTypes.WARNING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStepOneInvalid, isStepTwoInvalid, step]);


  const onStepProgressionClick = (localStep: number) => {
    if (localStep >= 2) {
      mountingStepTwo(true);
      trigger(["procedure_attributes", "prescription_attributes", "shipments_attributes"]);
      if (localStep > 2)
        trigger(["doctor_attributes", "patient_attributes", "insurances_attributes"])
    }

    changeStep(localStep);
  };

  const onSubmitCase = (values: AddCaseProps) => {
    if (
      (pcc_outreach_value === true && pcc_value === true) ||
      ["Other", "PHN"].includes(prescriptionData.indication)
    ) {
      caseDataSubmission(values)
    } else {
      if (isAlreadyShown) caseDataSubmission(values)
      else setShouldShowModal(true);
    }
  }





  return (
    <div className="d-flex flex-fill overflow-auto">
      <div className="container container-short d-flex flex-column flex-fill">
        <AuthSubHeader />

        <form onSubmit={handleSubmit(onSubmitCase)} ref={formRef} className="d-flex flex-fill flex-column">
          <div className="bg-light py-3 sticky-top top-0 z-1">
            <div id="case-creation-progress-bar" className="case-progress-bar">
              <button
                type="button"
                id="progress-step-1"
                className={`btn case-creation-progress-step ${step >= 1 ? isStepOneInvalid ? 'invalid-progress-step' : 'active-progress-step' : ''}`}
                onClick={() => onStepProgressionClick(1)}
              >
                1. Getting Started <i className={`bi bi-play-fill progression-arrow-head`}></i>
              </button>

              <button
                type="button"
                id="progress-step-2"
                className={`btn case-creation-progress-step ${step >= 2 ? isStepTwoInvalid ? 'invalid-progress-step' : 'active-progress-step' : ''}`}
                onClick={() => onStepProgressionClick(2)}
              >
                2. Patient Information <i className={`bi bi-play-fill progression-arrow-head`}></i>
              </button>

              <button
                type="button"
                id="progress-step-3"
                className={`btn case-creation-progress-step ${step >= 3 ? 'active-progress-step' : ''}`}
                onClick={() => onStepProgressionClick(3)}
              >
                3. Finish
              </button>
            </div>
          </div>

          {userData ? (
            <>
              <Controller
                name="creator_name"
                control={control}
                defaultValue={userData.name}
                render={() => (<></>)}
              />

              <Controller
                name="user_id"
                control={control}
                defaultValue={userData.id}
                render={() => (<></>)}
              />
            </>
          ) : (<></>)}

          <Controller
            name="category"
            control={control}
            render={() => <></>}
          />

          <div id="step-1" className="case-creation-step">
            {step >= 1 && <CaseStepOne control={control} watch={watch} setValue={setValue} getValues={getValues} isVisible={step === 1} />}
          </div>
          <div id="step-2" className="case-creation-step">
            {isStepTwoMounted && <CaseStepTwo control={control} getValues={getValues} watch={watch} setValue={setValue} isVisible={step === 2} trigger = {trigger}/>}
          </div>
          <div id="step-3" className="case-creation-step">
            {step === 3 && <CaseStepThree control={control} getValues={getValues} />}
          </div>

          <div className="mt-2 d-flex mb-3">
            <button type="button" key="previousStepButton"
              className={`me-3 btn btn-outline-secondary ${step === 1 ? 'd-none' : ''}`}
              onClick={() => onStepProgressionClick(step - 1)}
            >
              <i className="bi bi-chevron-double-left"></i> Previous
            </button>

            {step === 3 ? (
              <button type="submit" disabled={addCaseLoading} key="formSubmitButton"
                className="btn btn-blue d-flex"
              >
                {addCaseLoading ? (
                  <i className="spinner-border text-white me-2" style={{width: `24px`, height: `24px`}}></i>
                ) : null}
                <p className="col-auto mb-0">Submit Request</p>
              </button>
            ) : (
              <button type="button" key="nextStepButton" className="btn btn-outline-secondary"
                onClick={() => onStepProgressionClick(step + 1)}
              >
                Next <i className="bi bi-chevron-double-right"></i>
              </button>
            )}
          </div>
        </form>
        <div className="mb-4"><FooterText /></div>

        <RestoreCaseSessionModal
          isModalVisible={isModalVisible}
          removeCurrentData={removeCurrentData}
          restoringCaseData={restoringCaseData}
        />
      </div>

      <UploadClinicalModal
        addSkipForNow={true}
        caseId={precertInfoModal.caseId}
        precertId={0}
        isModalVisible={precertInfoModal.isModalVisible}
        hideModal={() => {
          setPrecertInfoModal({
            isModalVisible: false,
            caseId: 0,
            precertId: 0,
          })
          navigate('/cases');
        }}
      />

      <StepThreeConfirmationModal
        isModalVisible={shouldShowModal}
        hideModal={() => {
          setShouldShowModal(false)
        }}
        hideModalOnConfirmation={() => {
          setShouldShowModal(false)
          setIsAlreadyShown(true)
          formRef.current?.requestSubmit()
        }}
      />
    </div>
  );
};

export default HubCaseClone;
